import { MutableRefObject, useRef, useState } from "react"
import './ModePositionsExercise.scss'
import { produce } from "immer"
import { IntervalLines } from "../../../Comps/Connectors/IntervalLines";
import { HxMapGroup } from "../../../Comps/HxMap/HxMapGroup";
import { HexMap } from "../../../Data/HexMap/HexMap";
import { HexMapBuilderParams, BuildHexMap } from "../../../Data/HexMap/HexMapBuilder";
import { HexMapModifier } from "../../../Data/HexMap/HexMapModifier";
import { Note } from "../../../Data/HexMap/Note";
import { All } from "../../../Util/Constants/Quantities";
import { TransformTranslate } from "../../../Util/Transform/Transform";
import { PositionSelector } from "../../../Util/PositionSelector/PositionSelector";
import { OutlineBox } from "../../../Comps/OutlineBox/OutlineBox";
import { ExerciseMessage } from "../Utils/Message/ExerciseMessage";
import { useNotePlayer } from "../../../Sound/SoundStore";
import { NotePlayer } from "../../../Sound/NotePlayer";
import { Modes } from "../../../Util/Music/Modes";
import { Capitalize } from "../../../Util/Strings/Capitalize";

export function ModePositionsExercise(props:{tonic:number}){
    const notePlayer:NotePlayer = useNotePlayer()
    const maxSelectionHistory:number = 4
    const positionSelector:MutableRefObject<PositionSelector> = useRef(new PositionSelector(maxSelectionHistory))
    const selectedPosition:MutableRefObject<number> = useRef(positionSelector.current.getRandomPosition(-1))
    const answerIs:MutableRefObject<boolean|null> =  useRef(null)
    const radius:number = 130
    const hexWidth:number = radius * 2.8
    const hexHeight:number = radius * 2.8
    const modeDescriptions:string[] = [
        'Standard Major',
        'Light Minor',
        'Heavy Minor',
        'Light Major',
        'Heavy Major',
        'Standard Minor',
        'Inharmonic Minor'
    ]
    const getMessage = () => {
        if(answerIs.current === null){
            return(
            <span>
                <span>Where is the root of </span>
                <strong> {Capitalize(Modes.Names[selectedPosition.current])}</strong>
                <span>{`, the ${modeDescriptions[selectedPosition.current]} mode?`}</span>
            </span>
            )
        }
        if(answerIs.current === true){
            return `Correct!`
        }
        return `Incorrect.`
    }
    const [message, setMessage] = useState(getMessage())
    const showNextPosition = () => {
        answerIs.current = null
        selectedPosition.current = positionSelector.current.getRandomPosition(selectedPosition.current)
        update()
    }
    const playModalScale = (modalIndex:number) => {
        const notes:number[] = Modes.getParallelModeScale(props.tonic, modalIndex, true)
        notePlayer.playNotes(notes, true, 300)
    }
    const onCorrect = (note:Note) => {
        playModalScale(note.params.noteIndex)
        answerIs.current = true
        update()
        setTimeout(showNextPosition, 1500)
    }
    const onIncorrect = () => {
        notePlayer.playBuzzer()
        answerIs.current = false
        setMessage(getMessage())
        setTimeout(()=>{answerIs.current = null; update()}, 1500)
    }
    const onNoteClick = (note:Note) => {
        if(note.params.noteIndex === selectedPosition.current){onCorrect(note)}
        else{ onIncorrect()}
    }
    const onKeyClick = () => {}
    const getMap = () => {
        const params:HexMapBuilderParams = {
            rows:1, columns:1, tonic:props.tonic, radius:radius
        }
        const map:HexMap = BuildHexMap(params)
        const mod:HexMapModifier = new HexMapModifier(map)
        mod.notes_setDefined([All], false)
        mod.notes_setPrimaryLabelAsPosition([All])
        return map
    }
    const [map, setMap] = useState(getMap())
    const mod:MutableRefObject<HexMapModifier> = useRef(new HexMapModifier(map))
    const update = () => {
        setMessage(getMessage())
        setMap(produce((draft)=>{
            mod.current.setDraft(draft)
            mod.current.notes_setDefined([All], false)
            mod.current.notes_setSelectedState([All], Note.SelectedState_Default)
            if(answerIs.current === true){
                mod.current.notes_setDefined([selectedPosition.current], true)
                mod.current.notes_setSelectedState([selectedPosition.current], Note.SelectedState_Hilited)
            }
        }))
    }
    const lines:MutableRefObject<JSX.Element> = useRef(<IntervalLines notes={map.notes} displayConfig={map.params.displayConfig}/>)
    return(
        <OutlineBox title={'The Mode Positions Exercise'} width={600}>
            <div className={'mode-positions'}>
                <ExerciseMessage key={Date.now()} message={message} answerStatus={answerIs.current}/>              
                <div>
                    <svg width={hexHeight} height={hexWidth} className={''}>
                        <g transform={TransformTranslate(hexWidth/2,hexHeight/2)}>
                            <HxMapGroup onNoteClick={onNoteClick} onKeyClick={onKeyClick} hexMap={map}>
                                {lines.current}
                            </HxMapGroup>
                        </g>
                    </svg>
                </div>
            </div>
        </OutlineBox>
        
    )
}